// WU-HOW: the route the pointer travels through the five "How it works" steps, as pure geometry (no DOM), so
// it can be tested in jsdom. Nothing on this route is drawn: only the pointer moves along it. The hook measures
// the cards and their icon badges; this module turns those boxes into one path in real pixels, from badge
// centre to badge centre, kept out of the text.

export type Point = { x: number; y: number };
export type Box = { left: number; top: number; right: number; bottom: number };

export type JourneyInput = {
  cards: Box[];
  /** Icon badge centres, one per card, in step order. */
  nodes: Point[];
  badgeRadius: number;
  width: number;
  height: number;
};

export type JourneyMode = 'rows' | 'stack';

export type Journey = {
  mode: JourneyMode;
  /** The pointer's route, from the centre of badge 1 to the centre of badge 5. */
  travel: string;
  /** Where each step's badge centre sits along `travel`, from 0 (step 1) to 1 (step 5). */
  stops: number[];
  /** Where the route reaches the edge of badges 2–5 (the pointer fades out here, before the icon). */
  arrive: number[];
  /** Where the route leaves the edge of badges 1–4 (the pointer reappears here, past the icon). */
  leave: number[];
  width: number;
  height: number;
};

/**
 * The pointer reaches step n at (n − 1) × STEP_SECONDS into each LOOP_SECONDS loop, and each card is lit from
 * the pointer's arrival until it reaches the next step. design-system.css (journey-pointer, journey-lit) uses
 * the same numbers: 1.6 s is 16 % of the 10 s loop.
 */
export const LOOP_SECONDS = 10;
export const STEP_SECONDS = 1.6;

/** Distance of the 3 → 4 turn from card 3's right edge: inside its 28 px padding, clear of the text. */
export const DROP_INSET = 8;
/** How far the hops between neighbouring steps lift into the cards' empty top padding. */
export const ARC_LIFT = 24;
const BADGE_GAP = 6;
const BEND_RADIUS = 32;
/** The S-wave down the badge column below 1024 px (the route moves about 0.29 of this either side). */
const RAIL_WAVE = 22;
const CUBIC_SAMPLES = 24;

export type Segment =
  | { kind: 'L'; from: Point; to: Point }
  | { kind: 'C'; from: Point; c1: Point; c2: Point; to: Point };

const line = (from: Point, to: Point): Segment => ({ kind: 'L', from, to });

function cubicAt(s: Extract<Segment, { kind: 'C' }>, t: number): Point {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return {
    x: a * s.from.x + b * s.c1.x + c * s.c2.x + d * s.to.x,
    y: a * s.from.y + b * s.c1.y + c * s.c2.y + d * s.to.y,
  };
}

function segmentLength(s: Segment): number {
  if (s.kind === 'L') return Math.hypot(s.to.x - s.from.x, s.to.y - s.from.y);
  let total = 0;
  let previous = s.from;
  for (let i = 1; i <= CUBIC_SAMPLES; i += 1) {
    const point = cubicAt(s, i / CUBIC_SAMPLES);
    total += Math.hypot(point.x - previous.x, point.y - previous.y);
    previous = point;
  }
  return total;
}

/** Points along segments, for tests and checks. */
export function samplePoints(segments: Segment[], perSegment = CUBIC_SAMPLES): Point[] {
  const points: Point[] = [];
  segments.forEach((s) => {
    for (let i = 0; i <= perSegment; i += 1) {
      const t = i / perSegment;
      points.push(
        s.kind === 'L' ? { x: s.from.x + (s.to.x - s.from.x) * t, y: s.from.y + (s.to.y - s.from.y) * t } : cubicAt(s, t),
      );
    }
  });
  return points;
}

const n = (value: number) => Number(value.toFixed(1));
const xy = (p: Point) => `${n(p.x)} ${n(p.y)}`;

function toPath(segments: Segment[]): string {
  if (segments.length === 0) return '';
  const parts = [`M ${xy(segments[0].from)}`];
  segments.forEach((s) => {
    parts.push(s.kind === 'L' ? `L ${xy(s.to)}` : `C ${xy(s.c1)} ${xy(s.c2)} ${xy(s.to)}`);
  });
  return parts.join(' ');
}

/** 'rows' when steps 1–3 share a row and step 4 starts below it (the 3 + 2 zig-zag); otherwise 'stack'. */
export function journeyMode(cards: Box[]): JourneyMode {
  if (cards.length < 5) return 'stack';
  const sameRow = Math.abs(cards[0].top - cards[1].top) <= 1 && Math.abs(cards[1].top - cards[2].top) <= 1;
  return sameRow && cards[3].top >= cards[2].bottom - 1 ? 'rows' : 'stack';
}

/** From a badge centre out to its edge, along the connector, and in to the next badge centre. */
function leg(from: Point, connector: Segment[], to: Point): Segment[] {
  return [line(from, connector[0].from), ...connector, line(connector[connector.length - 1].to, to)];
}

/** A soft hop from one badge to the next in a row, lifting into the empty top padding of the cards. */
function hop(a: Point, b: Point, stop: number): Segment[] {
  const from = { x: a.x + stop, y: a.y };
  const to = { x: b.x - stop, y: b.y };
  const reach = (to.x - from.x) * 0.3;
  return [{ kind: 'C', from, c1: { x: from.x + reach, y: from.y - ARC_LIFT }, c2: { x: to.x - reach, y: to.y - ARC_LIFT }, to }];
}

function rowsLegs({ cards, nodes, badgeRadius }: JourneyInput): Segment[][] {
  const stop = badgeRadius + BADGE_GAP;
  const [n1, n2, n3, n4, n5] = nodes;

  // 3 → 4: rise and roll over into card 3's right padding, down it, one long swoosh through the row gap,
  // and down into card 4. All of it is clear of the text.
  const dropX = cards[2].right - DROP_INSET;
  const gapTop = cards[2].bottom;
  const gapBottom = Math.min(cards[3].top, cards[4].top);
  const span = gapBottom - gapTop;
  const start = { x: n3.x + stop, y: n3.y };
  const bend = Math.max(4, Math.min(BEND_RADIUS, gapTop - n3.y - 4));
  const turnTo = { x: dropX, y: n3.y + bend };
  const swooshFrom = { x: dropX, y: gapTop };
  const swooshTo = { x: n4.x, y: gapBottom };
  const descent: Segment[] = [
    {
      kind: 'C',
      from: start,
      c1: { x: start.x + (dropX - start.x) * 0.35, y: n3.y - ARC_LIFT },
      c2: { x: dropX, y: n3.y - ARC_LIFT * 0.4 },
      to: turnTo,
    },
    line(turnTo, swooshFrom),
    {
      kind: 'C',
      from: swooshFrom,
      c1: { x: dropX, y: gapTop + span * 0.75 },
      c2: { x: n4.x, y: gapTop + span * 0.25 },
      to: swooshTo,
    },
    line(swooshTo, { x: n4.x, y: n4.y - stop }),
  ];

  return [
    leg(n1, hop(n1, n2, stop), n2),
    leg(n2, hop(n2, n3, stop), n3),
    leg(n3, descent, n4),
    leg(n4, hop(n4, n5, stop), n5),
  ];
}

/** Below 1024 px: down the badge column with a gentle S-wave, never leaving the column. */
function stackLegs({ nodes, badgeRadius }: JourneyInput): Segment[][] {
  const stop = badgeRadius + BADGE_GAP;
  return nodes.slice(0, -1).map((a, i) => {
    const b = nodes[i + 1];
    const from = { x: a.x, y: a.y + stop };
    const to = { x: b.x, y: b.y - stop };
    const third = (to.y - from.y) / 3;
    const wave: Segment = {
      kind: 'C',
      from,
      c1: { x: from.x + RAIL_WAVE, y: from.y + third },
      c2: { x: to.x - RAIL_WAVE, y: to.y - third },
      to,
    };
    return leg(a, [wave], b);
  });
}

/** The legs between neighbouring steps, exposed for tests. */
export function journeyLegs(input: JourneyInput): { mode: JourneyMode; legs: Segment[][] } {
  const mode = journeyMode(input.cards);
  return { mode, legs: mode === 'rows' ? rowsLegs(input) : stackLegs(input) };
}

export function pathLength(segments: Segment[]): number {
  return segments.reduce((total, s) => total + segmentLength(s), 0);
}

/**
 * Where the route crosses each badge edge. Every leg is [centre → edge, the hop, edge → centre], so the pointer
 * can fade before an icon and reappear after it, and never be seen on top of one.
 */
export function edgeFractions(legs: Segment[][]): { arrive: number[]; leave: number[] } {
  const total = legs.reduce((sum, value) => sum + pathLength(value), 0) || 1;
  const arrive: number[] = [];
  const leave: number[] = [];
  let walked = 0;
  legs.forEach((value) => {
    const out = segmentLength(value[0]);
    const inward = segmentLength(value[value.length - 1]);
    const length = pathLength(value);
    leave.push((walked + out) / total);
    arrive.push((walked + length - inward) / total);
    walked += length;
  });
  return { arrive, leave };
}

/** Each step's position along the route, 0 at step 1 and 1 at step 5. */
export function stopFractions(legs: Segment[][]): number[] {
  const lengths = legs.map((value) => pathLength(value));
  const total = lengths.reduce((sum, value) => sum + value, 0) || 1;
  const stops = [0];
  let walked = 0;
  lengths.forEach((value) => {
    walked += value;
    stops.push(walked / total);
  });
  return stops;
}

export function buildJourney(input: JourneyInput): Journey | null {
  if (input.nodes.length !== 5 || input.cards.length !== 5 || input.width <= 0 || input.height <= 0) return null;
  const { mode, legs } = journeyLegs(input);
  return {
    mode,
    travel: toPath(legs.flat()),
    stops: stopFractions(legs),
    ...edgeFractions(legs),
    width: input.width,
    height: input.height,
  };
}
